/* Live transcript pane — subscribes to /api/calls/:id/stream and renders
 * per-turn bubbles. Same data feed the Inbound/Outbound transcripts use,
 * just laid out for the Agent Desk's narrow right-rail.
 *
 * Post-0024 the role is the physical speaker:
 *   customer  → the calling party
 *   agent     → the human agent (only on agent-desk listen-only calls)
 *   assistant → the AI bot (only on AI-handled calls)
 *   system    → centered pill for system notices
 */

import { useEffect, useRef, useState } from "react";

import { API_URL, fmtTime } from "../lib/api";

type Role = "customer" | "agent" | "assistant" | "system";

type Turn = {
  id: number;
  ts: string;
  role: Role;
  text: string;
};

const ROLE_LABEL: Record<Role, string> = {
  customer:  "CUSTOMER",
  agent:     "AGENT",
  assistant: "AI",
  system:    "SYSTEM",
};

export function Transcript({ callId }: { callId: number | undefined }) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (callId === undefined) {
      setTurns([]);
      setLoading(false);
      setErr(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setErr(null);

    // Initial backfill — picks up the call's history if we joined late.
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/calls/${callId}/transcripts`);
        if (!r.ok) throw new Error(`transcripts ${r.status}`);
        const data = (await r.json()) as { transcripts: Turn[] };
        if (!cancelled) setTurns(data.transcripts ?? []);
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Live updates — same SSE the call detail panes use.
    const es = new EventSource(`${API_URL}/api/calls/${callId}/stream`);
    es.addEventListener("transcript", (ev) => {
      try {
        const row = JSON.parse((ev as MessageEvent).data) as Turn;
        setTurns((cur) =>
          cur.some((x) => x.id === row.id) ? cur : [...cur, row],
        );
      } catch {
        /* malformed event, skip */
      }
    });
    return () => {
      cancelled = true;
      es.close();
    };
  }, [callId]);

  // Auto-scroll to the latest turn as the call progresses.
  useEffect(() => {
    const el = bodyRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  if (callId === undefined) {
    return (
      <div id="agent-desk-transcript-body" className="agent-desk-transcript-body">
        <div className="agent-desk-transcript-empty">
          Live transcript appears here when a call is connected.
        </div>
      </div>
    );
  }

  return (
    <div
      ref={bodyRef}
      id="agent-desk-transcript-body"
      className="agent-desk-transcript-body"
    >
      {loading && turns.length === 0 && (
        <div className="agent-desk-transcript-empty">Loading transcript…</div>
      )}
      {err && (
        <div id="agent-desk-transcript-err" className="dial-err">{err}</div>
      )}
      {turns.length === 0 && !loading && !err && (
        <div className="agent-desk-transcript-empty">No turns yet.</div>
      )}
      {turns.map((t) => (
        <TurnRow key={t.id} turn={t} />
      ))}
    </div>
  );
}

function TurnRow({ turn }: { turn: Turn }) {
  if (turn.role === "system") {
    return (
      <div
        id={`agent-desk-transcript-turn-${turn.id}`}
        className="agent-desk-transcript-turn agent-desk-transcript-turn-system"
      >
        <span className="agent-desk-transcript-pill">{turn.text}</span>
      </div>
    );
  }
  return (
    <div
      id={`agent-desk-transcript-turn-${turn.id}`}
      className={`agent-desk-transcript-turn agent-desk-transcript-turn-${turn.role}`}
    >
      <div className="agent-desk-transcript-meta">
        <span className="agent-desk-transcript-role">
          {ROLE_LABEL[turn.role]}
        </span>
        <span className="agent-desk-transcript-time">{fmtTime(turn.ts)}</span>
      </div>
      <div className="agent-desk-transcript-bubble">{turn.text}</div>
    </div>
  );
}
